import { styled } from 'linaria/react';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import { useVariantFromUrl } from '@jetshop/core/hooks/useProductVariants/useVariantFromUrl';
import { useIntl } from '@jetshop/intl';
import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute';

import MaxWidth from '../Layout/MaxWidth';
import AddToCartForm from './AddToCart/AddToCartForm';
import ImageContainer from './Images/ImageContainer';
import ProductInfo, { ProductSection } from './ProductInfo';
import ProductPageLoadingState from './ProductPageLoadingState';
import { RelatedProducts } from './RelatedProducts';
import { Container } from './styledComponents';
import { Price } from '@jetshop/ui/Price';
import ProductConfigurationContext from '@jetshop/core/components/ProductConfigurationProvider/ProductConfigurationContext';
import { useDynamicPrice } from '@jetshop/core/hooks/useDynamicPrice';
import { usePrice } from '@jetshop/core/hooks/usePrice';

import { FindifyRecommendations, FindifyTracker } from '../Findify';
import { theme } from '../Theme';
import { Above, Below } from '@jetshop/ui/Breakpoints';
import ProductMetas from './ProductMetas';
import Hreflang from '../Hreflang';
import DiscountBadge from '../../components/DiscountBadge';
import { useAppSettingsStore } from '../../appSettingsStore';
import { Helmet } from 'react-helmet-async';
import { ReactComponent as Close } from '../../svg/close.svg';
import SizeGuide from './SizeGuide';

const StyledContainer = styled(Container)`
  .testfreaks-badge {
    margin-bottom: 14px;
    letter-spacing: -0.02em !important;
  }
  .product-details .testfreaks .tf-stars-fg {
    background-image: url('data:image/svg+xml;utf8,<svg width="11" height="10" viewBox="0 0 11 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5262 3.81049L6.74204 3.48418L5.26311 0L3.78417 3.48944L0 3.81049L2.87366 6.29994L2.01051 9.9999L5.26311 8.03676L8.51571 9.9999L7.65782 6.29994L10.5262 3.81049Z" fill="black"/></svg>') !important;
  }
  .testfreaks .tf-score  {
    margin-right: 4px !important;
  }
  .testfreaks-badge .tf-stars-svg {
    width: 11px !important;
    height: 10px !important;
    background-size: 11px !important;
  }
  .testfreaks-badge .tf-rating {
    margin-right: 4px !important;
    font-size: 12px !important;
    letter-spacing: -0.02em !important;
    font-weight: 400 !important;
  }
  .testfreaks .tf-link {
    font-size: 12px !important;
    letter-spacing: -0.02em !important;
  }
  .testfreaks .tf-link::before {
    content: '(' !important;
  }

  .testfreaks .tf-link::after {
    content: ')' !important;
  }
  padding: 0;
  max-width: unset;
  ${theme.below.md} {
    display: contents;

    .product-details-wrapper,
    .product-details,
    .cta-wrapper,
    .cta-wrapper > div {
      display: contents;
    }
    .cta-button-wrapper {
      position: sticky;
      bottom: 0;
      z-index: 4;
      top: 6rem;
      padding: 1rem;
      background: #fff;
      ${theme.below.sm} {
        top: 3.85rem;
      }
    }
    .cta-button-wrapper > div {
      margin-top: 0;
    }
    .product-details-wrapper,
    .variant-selector,
    .product-info,
    header {
      width: 100%;
      padding: 0 1rem;
    }
  }
`;
const ProductContainer = styled('div')`
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;

  .layout-container {
    align-items: flex-start;
  }
  h1,
  p.findify-layouts--recommendation--slider__title {
    font-size: 26px;
    font-weight: 600;
    line-height: 26px;
    text-transform: capitalize;
    letter-spacing: -0.02em;
  }

  p {
    font-size: 0.875rem;
    line-height: 1.58;
    color: ${theme.colors.text};
    a,
    a:visited {
      color: ${theme.colors.text};
      text-decoration: underline;
    }
    a:hover,
    a:focus {
      color: ${theme.colors.black};
    }
  }

  header {
    width: 100%;
  }
  .gallery-wrapper {
    position: relative;
    .image-gallery-content:not(.fullscreen) {
      .image-gallery-slides {
        margin: auto;
        ${theme.below.md} {
          max-width: 600px;
        }
      }
    }
  }
  .customer-comment-input-wrapper {
    width: 100%;
    .customer-comment-input {
      height: 44px;
    }
    .status-wrapper {
      top: -39px;
    }
  }
  .quantity-input-wrapper {
    margin-bottom: 0rem;
    margin-right: 10px;
    width: 54px;
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    input[type='number'] {
      border-radius: 4px;
      -moz-appearance: textfield; /* Firefox */
    }
    input {
      width: 54px;
      height: 54px;
      text-align: center;
      padding: 0px;
      margin-bottom: 0px;
    }
    label {
      text-align: center;
    }
  }
  .discountBadge-save {
    margin: 0 0 0.5rem;
  }
  .discountBadge-percentage {
    position: absolute;
    left: 1rem;
    top: 1rem;
    z-index: 2;
  }
  .product-details {
    ${theme.above.md} {
      align-items: flex-start;
    }

    .price,
    .new-price,
    .old-price {
      font-size: 14px;
      display: inline-block;
    }
    .new-price {
      margin-right: 0.5rem;
      color: ${theme.colors.black};
    }
    .old-price {
      color: ${theme.colors.text};
    }
  }

  .post-note {
    font-size: 12px;
    letter-spacing: -0.02em;
    color: ${theme.colors.text};
    display: block;
    margin-bottom: 32px;
    ${theme.below.md} {
      width: 100%;
      padding-left: 16px;
    }
  }

  .customer-photos-text {
    margin-bottom: 1rem;
  }
  ${theme.above.md} {
    .product-details-wrapper {
      position: sticky;
      top: 104px;
      right: 0;
      min-height: calc(100vh - 100px);
      padding: 80px;
      background-color: ${theme.colors.lightBackground};
    }
  }
  .findify-product-footer {
    position: relative;
    z-index: 1;
  }
  .size-guide-wrapper {
    margin-bottom: 24px;
    color: #535353;
  }
  .size-guide-wrapper > span {
    font-size: 12px !important;
    width: fit-content;
  }
`;

const PriceFlex = styled.div`
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 24px;
`;

const PriceHistoryLink = styled.span`
  font-size: 10px;
  margin-left: 12px;
  cursor: pointer;
`;

const PriceHistory = styled.div`
  background: ${theme.colors.lightGrey};
  padding: 4px 8px;
  font-size: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const PriceHistoryClose = styled.div`
  width: 12px;
  height: 12px;
  cursor: pointer;
  display: flex;
`;

const Product = ({ result: { data, loading }, product }) => {
  const initialVariant = useVariantFromUrl();
  const t = useIntl();
  const [openReviews, setopenReviews] = useState(false);

  const variantHandler = useProductVariants(product, {
    initialVariant,
    preselectCheapest: true
  });
  const [showPreviousPrice, setShowPreviousPrice] = useState(false);

  const configurationContext = useContext(ProductConfigurationContext);
  const price = useDynamicPrice(product, selectedVariation);
  const discountPercentage =
    price?.previousPrice?.incVat !== price.price?.incVat &&
    100 - Math.round((price.price.incVat / price.previousPrice.incVat) * 100);
  const { formattedPricing: discountSave } = usePrice({
    price: price?.previousPrice?.incVat !== price.price?.incVat && {
      incVat: Math.round(price.previousPrice.incVat - price.price.incVat)
    }
  });
  const freeShippingReturns = useAppSettingsStore(
    state => state.freeShippingReturns
  );
  const sizingTable = product.customFields.find(
    f => f.key === 'sizeguide_table'
  );

  const {
    selectedVariant: selectedVariation,
    getMissingOptions
  } = variantHandler;

  const lowestPrice = useMemo(() => {
    return selectedVariation?.history?.previousPrice?.reduce((a, b) => {
      return a > b?.price?.incVat ? b?.price?.incVat : a;
    }, selectedVariation?.history?.previousPrice?.[0]?.price?.incVat);
  }, [selectedVariation]);

  useEffect(() => {
    return () => {
      let testFreaks = window?.testFreaks || [];
      testFreaks?.push(['reset']);
    };
  }, []);
  if (!product)
    return (
      <ProductContainer>
        <ProductPageLoadingState />
      </ProductContainer>
    );

  const images = product.images ?? [];
  const parents = getParentOrCanonicalRoute(data?.route?.parents, product);

  const relatedProducts = product.relatedProducts;

  const { alternateRoutes } = data?.route || {};
  console.log('lowestPrice', lowestPrice);
  return (
    <ProductContainer>
      <FindifyTracker
        isProdPage={true}
        product={product}
        selectedVariation={selectedVariation}
      />
      <Hreflang alternateRoutes={alternateRoutes} />
      <ProductMetas product={product} />
      {selectedVariation?.articleNumber && (
        <Helmet>
          <script type="text/javascript">
            {`
          testFreaks = window.testFreaks || []; 
          testFreaks.push(["setProductId", "${selectedVariation?.articleNumber}"]);
          testFreaks.push(["setAnalytics", "dataLayer"]);
          testFreaks.push(["load", ["badge", "reviews", "qa", "seller", "videos"]]);
          testFreaks.push(["onBadgeClick", function() {
            const element = document.getElementById("reviews");
            element.scrollIntoView({ block: "start" });
          }]);
        `}
          </script>
        </Helmet>
      )}
      <Below breakpoint="sm">
        <ProductSection className="gallery-wrapper">
          {images && (
            <ImageContainer
              images={images}
              variant={selectedVariation}
              badges={product.badges}
            >
              {discountPercentage ? (
                <DiscountBadge className="discountBadge-percentage">
                  {discountPercentage}%
                </DiscountBadge>
              ) : null}
            </ImageContainer>
          )}
        </ProductSection>
      </Below>
      <StyledContainer className="layout-container">
        <Above breakpoint="sm">
          <ProductSection className="gallery-wrapper">
            {images && (
              <ImageContainer
                images={images}
                variant={selectedVariation}
                // badges={product.badges}
                badges={['Badge test']}
              >
                {discountPercentage ? (
                  <DiscountBadge className="discountBadge-percentage">
                    {discountPercentage}%
                  </DiscountBadge>
                ) : null}
              </ImageContainer>
            )}
          </ProductSection>
        </Above>
        <ProductSection className="product-details-wrapper">
          <div className="product-details">
            <header>
              <div id="testfreaks-badge" onClick={() => setopenReviews(true)} />
              <h1 data-testid="product-title">
                {product.name || 'Loading...'}
              </h1>
              <PriceFlex>
                <Price
                  {...price}
                  css={{
                    opacity:
                      configurationContext && configurationContext.loading
                        ? 0.3
                        : 1
                  }}
                />
                {lowestPrice ? (
                  <PriceHistoryLink onClick={() => setShowPreviousPrice(true)}>
                    {t('priceHistory')}
                  </PriceHistoryLink>
                ) : null}
              </PriceFlex>
              {showPreviousPrice && lowestPrice && (
                <PriceHistory>
                  <p>
                    {t('lowest_price_30_days')}: {lowestPrice}kr
                  </p>
                  <PriceHistoryClose
                    onClick={() => setShowPreviousPrice(false)}
                  >
                    <Close />
                  </PriceHistoryClose>
                </PriceHistory>
              )}
              <div id="testfreaks-fit" />
              {sizingTable && (
                <div className="size-guide-wrapper">
                  <span>{t('help_to_find_your_size_use_our')}</span>
                  <SizeGuide product={product} />
                </div>
              )}
              <div id="testfreaks-fit" />
            </header>
            <div className="cta-wrapper">
              <AddToCartForm
                product={product}
                variant={selectedVariation}
                getMissingOptions={getMissingOptions}
                variantHandler={variantHandler}
              >
                <span className="post-note">{freeShippingReturns}</span>
              </AddToCartForm>
            </div>
          </div>
          <ProductInfo
            openReviews={openReviews}
            setopenReviews={setopenReviews}
            className="product-info"
            product={product}
          />
          <div id="reviews"></div>
        </ProductSection>
      </StyledContainer>
      {relatedProducts && <RelatedProducts {...{ relatedProducts, loading }} />}
      <MaxWidth>
        <div className="findify-product-footer">
          <FindifyRecommendations
            recIdString={'product-findify-rec-5'}
            product={product}
          />
          <FindifyRecommendations
            recIdString={'product-findify-rec-6'}
            product={product}
          />
        </div>
      </MaxWidth>
    </ProductContainer>
  );
};

export default Product;
